<template>
  <div class="document-container">
    <es-remove-location-modal
      v-if="showModal"
      @closeModal="toggleModal"
      @updateList="updateLocationList"
      :groupId="modalLocation.locationGroupId"
      :locationId="modalLocation.location.stateLocationId"
      :parentId="modalLocation.parentId"
      :childLevel="modalLocation.childLevel"/>
    <div class="document">
      <div class="header">
        <EsHeader>
          <template #header>
            <div class="header-title">Location List</div>
            <div class="header-subtitle">View/edit location list</div>
          </template>
        </EsHeader>
      </div>
      <div class="content">
        <es-loader v-if="this.loading === true" style="margin: 3rem"/>
        <div v-else>
          <es-location-add-row
            :disableEdits="disableEdits"
            :location="location"
            :childLevel="childLevel"
            :parentId="parentId"
            @onEdit="disableAllEdits"
            @reloadLocations="reloadLocations"/>
          <table class="table is-fullwidth"
            :class="{ 'is-hoverable' : locations.length> 0 }">
            <thead>
              <tr>
                <th title="Location Name">
                  <a class="sort-by"
                    @click.prevent="changeSort('name')">
                    Location Name
                  </a>
                </th>
                <th title="Start Date of the location associated to the list">
                  <a class="sort-by"
                    @click.prevent="changeSort('startDate')">
                    <span>Start Date</span>
                  </a>
                </th>
                <th title="End Date of the location associated to the list">
                  <a class="sort-by"
                    @click.prevent="changeSort('endDate')">
                    End Date
                  </a>
                </th>
                <th title="Number of active member locations">
                  <a class="sort-by"
                    @click.prevent="changeSort('count')">
                    Locations
                  </a>
                </th>
                <th title="Actions">Actions</th>
              </tr>
            </thead>
            <es-location-edit-row
              v-for="location in locations" v-bind:key="location.id"
              :location="location" :groupId="groupId" :disableEdits="disableEdits"
              @onEdit="disableAllEdits" @openModal="toggleModal"
              :class="{ 'highlightRow': stateLocationId === location.stateLocationId }"
              @reloadLocations="reloadLocations"/>
            <tfoot v-if="locations.length">
              <tr>
                <td colspan="100%">
                  <es-pager
                  :totalCount="totalLocations"
                  />
                </td>
              </tr>
            </tfoot>
            <tbody v-if="!locations.length">
              <tr>
                <td colspan="6" class="has-text-centered">
                  <div class="no-result-container">
                    <div class="icon">
                      <font-awesome-icon :icon="['fal', 'exclamation-triangle']" size="4x"/>
                    </div>
                    <p></p>
                    <h4>No location list available for {{ locationLabel ? locationLabel : groupLabel }}.</h4>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleRight,
  faEdit,
  faTimes,
  faExclamationTriangle,
} from '@fortawesome/pro-light-svg-icons';
import moment from 'moment';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsPager from '@/components/EsPager.vue';
import EsLocationAddRow from '@/components/EsLocationAddRow.vue';
import EsLocationEditRow from '@/components/EsLocationEditRow.vue';
import EsRemoveLocationModal from '@/components/EsRemoveLocationModal.vue';
import GroupLevelManager from '@/mixins/GroupLevelManager';

library.add(
  faAngleDoubleRight,
  faEdit,
  faTimes,
  faExclamationTriangle,
);

export default {
  mixins: [GroupLevelManager],
  components: {
    EsHeader,
    EsLoader,
    EsPager,
    EsLocationAddRow,
    EsLocationEditRow,
    EsRemoveLocationModal,
  },
  props: [
    'groupLabel',
    'childLevel',
    'parentId',
  ],
  data() {
    return {
      loading: false,
      locations: [],
      groupId: null,
      locationId: null,
      isEditing: false,
      disableEdits: null,
      showModal: false,
      stateLocationId: false,
      modalLocation: {},
      sortField: 'name',
      sortOrder: 'ASC',
      location: null,
      pageSize: 25,
      page: 1,
      totalLocations: 0,
      locationLabel: null,
    };
  },
  watch: {
    $route: 'fetchData',
  },
  computed: {
    requestParams() {
      const params = new URLSearchParams();
      if (this.$route.query.page) {
        params.append('page', this.$route.query.page);
      }
      params.append('pageSize', this.pageSize);
      const sorters = {};
      sorters.field = this.sortField;
      sorters.order = this.sortOrder;
      const sort = [];
      sort.push(sorters);
      if (sorters) params.append('sort', JSON.stringify(sort));

      if (this.locationId !== null) {
        params.append('parentId', this.locationId);
        params.append('childLevel', this.childLevel);
      }
      return params;
    },
  },
  mounted() {
    this.readParamsFromUrl();
    this.fetchData();
  },
  methods: {
    disableAllEdits() {
      this.disableEdits = !this.disableEdits;
    },
    toggleLoading() {
      this.loading = !this.loading;
    },
    toggleModal(location) {
      this.modalLocation = location;
      this.showModal = !this.showModal;
    },
    updateLocationList() {
      this.toggleModal();
      this.fetchData();
    },
    async reloadLocations(stateLocationId) {
      this.stateLocationId = stateLocationId;
      this.fetchData();
    },
    async fetchData() {
      this.$emit('updated');

      const url = `location-groups/${this.groupId}/location-group-mappings`;

      this.toggleLoading();
      this.readParamsFromUrl();

      try {
        const res = await axios.get(url, { params: this.requestParams }, { query: this.childLevel});
        this.locations = res.data.data;
        this.totalLocations = res.data.totalCount;
      } catch (err) {
        // handle error
      }

      if (this.locationId) {
        try {
          const res = await axios.get(`locations/${this.locationId}`);
          this.location = res.data;
          this.locationLabel = this.location.name;
        } catch (err) {
          // handle error
        }
      }
      this.setGroupLevels(this.location);
      this.toggleLoading();
      this.disableEdits = false;
    },
    readParamsFromUrl() {
      const { params } = this.$route;
      this.groupId = params.groupId;
      if (params.locationId) {
        this.locationId = params.locationId;
      } else {
        this.locationId = null;
        this.location = null;
      }
    },
    changeSort(field) {
      this.sortField = field;
      this.sortOrder = (this.sortOrder === 'ASC' ? 'DESC' : 'ASC');
      this.fetchData();
    },
    setGroupLevels(location) {
      if (this.location != null) {
        const { params } = this.$route;
        const { query } = this.$route;
        this.addLevels(
          {
            text: location.name,
            to:
            {
              name: 'groupLocationList',
              query,
              params,
            },
          },
        );
      } else {
        this.resetLevels();
        const { params } = this.$route;
        const { query } = this.$route;
        this.addLevels(
          {
            text: 'Location List',
            to:
            {
              name: 'groupLocationList',
              query: {
                childLevel: 0,
              },
              params,
            },
          },
        );
      }
    },
  },
  filters: {
    formatDate(value) {
      if (value) {
        return moment(value).format('MM/DD/YYYY');
      }
      return null;
    },
  },
};
</script>

<style lang="sass" scoped>
  .level
    margin-bottom: 0px
    padding-top: .5rem
    padding-bottom: .5rem
  .disabled-icon
    color: #999 !important
    pointer-events: none
  th
    white-space: nowrap
  .sort-by
    padding-right: 18px
    position: relative
    color: #777
    text-decoration: none
    &:before, &:after
      border: 4px solid transparent
      content: ""
      display: block
      height: 0
      right: 5px
      top: 50%
      position: absolute
      width: 0
    &:before
      border-bottom-color: #999
      margin-top: -9px
    &:after
      border-top-color: #999
      margin-top: 1px
  .no-result-container
    padding: 6.5rem

</style>
